import LocalActivityIcon from '@mui/icons-material/LocalActivity';
import { Typography } from '@mui/material';
import { MatchResultPlayerStatus } from '@okletsplay/core';
import { MatchStatus } from '@okletsplay/core/src/modules/match/enums';
import { MatchEntity } from 'app/interfaces/entity';
import { MatchTileStatus } from 'modules/matches/enums';
import { useMatchStatus } from 'modules/matches/hooks/use-match-status';
import React, { FC, useMemo } from 'react';
import { GameCurrencyWithFee } from 'modules/game-payments/components';
import { useStyles } from './MatchStatusText.styles';

interface MatchStatusTextProps {
  match: MatchEntity;
}

export const MatchTileStatusText: FC<MatchStatusTextProps> = ({ match }) => {
  const { classes } = useStyles();

  const { isPrivate, isCurrentUserPlayer, isCurrentUserOwner, currentUserResult, opponentResult } =
    useMatchStatus(match);

  const statuses = useMemo(
    () => ({
      [MatchTileStatus.Preparing]: {
        text: 'Preparing',
        className: classes.preparing,
      },
      [MatchTileStatus.Invited]: {
        text: 'Invited',
        className: classes.invited,
      },
      [MatchTileStatus.ReadyToPlay]: {
        text: 'READY TO PLAY',
        className: classes.readyToPlay,
      },
      [MatchTileStatus.Win]: {
        text: `W ${currentUserResult?.score ?? 0} - ${opponentResult?.score ?? 0}`,
        className: classes.win,
      },
      [MatchTileStatus.Lose]: {
        text: `L ${currentUserResult?.score ?? 0} - ${opponentResult?.score ?? 0}`,
        className: classes.lose,
      },
      [MatchTileStatus.Tied]: {
        text: `T ${currentUserResult?.score ?? 0} - ${opponentResult?.score ?? 0}`,
        className: classes.tied,
      },
      [MatchTileStatus.Forfeit]: {
        text: `FORFEIT`,
        className: classes.forfeit,
      },
      [MatchTileStatus.Aborted]: {
        text: `ABORTED`,
        className: classes.aborted,
      },
      [MatchTileStatus.Default]: {
        text: (
          <>
            <LocalActivityIcon className={classes.icon} fontSize="small" />
            <GameCurrencyWithFee amount={match.entryFee} showZeroLabel />
          </>
        ),
        className: classes.default,
      },
    }),
    [classes, match, currentUserResult, opponentResult]
  );

  const status = useMemo(() => {
    switch (true) {
      case match.status === MatchStatus.Finished &&
        currentUserResult?.status === MatchResultPlayerStatus.Win:
        return statuses[MatchTileStatus.Win];
      case match.status === MatchStatus.Finished &&
        currentUserResult?.status === MatchResultPlayerStatus.Lose:
        return statuses[MatchTileStatus.Lose];
      case match.status === MatchStatus.Finished &&
        currentUserResult?.status === MatchResultPlayerStatus.Tied:
        return statuses[MatchTileStatus.Tied];
      case match.status === MatchStatus.Finished &&
        currentUserResult?.status === MatchResultPlayerStatus.Forfeit:
        return statuses[MatchTileStatus.Forfeit];
      case [MatchStatus.Expired, MatchStatus.Aborted].includes(match.status):
        return statuses[MatchTileStatus.Aborted];
      case match.status === MatchStatus.Live:
      case match.status === MatchStatus.Lobby:
        return statuses[MatchTileStatus.ReadyToPlay];
      case isPrivate && isCurrentUserOwner && match.status === MatchStatus.Created:
        return statuses[MatchTileStatus.Preparing];
      case isPrivate && isCurrentUserPlayer && match.status === MatchStatus.Pending:
        return statuses[MatchTileStatus.Invited];
      case isPrivate && opponentResult?.status === MatchResultPlayerStatus.Win:
      case isPrivate && currentUserResult?.status === MatchResultPlayerStatus.Lose:
        return statuses[MatchTileStatus.Lose];
      case isPrivate && opponentResult?.status === MatchResultPlayerStatus.Lose:
      case isPrivate && currentUserResult?.status === MatchResultPlayerStatus.Win:
        return statuses[MatchTileStatus.Win];
      default:
        return statuses[MatchTileStatus.Default];
    }
  }, [
    statuses,
    isPrivate,
    match.status,
    opponentResult,
    currentUserResult,
    isCurrentUserOwner,
    isCurrentUserPlayer,
  ]);

  return (
    <Typography variant="subtitle2" className={status.className}>
      {status.text}
    </Typography>
  );
};
